import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { CheckCircle2, Loader2, Send } from 'lucide-react';
import { publicApi } from '@/lib/api';

const empty = {
    name: '',
    email: '',
    phone: '',
    donation_type: 'one-time',
    amount: '',
    message: '',
};

const TYPES = [
    { value: 'one-time', label: 'One-time donation' },
    { value: 'monthly', label: 'Monthly recurring' },
    { value: 'corporate-csr', label: 'Corporate CSR' },
    { value: 'in-kind', label: 'In-kind / material' },
];

const DonationForm = () => {
    const [form, setForm] = useState(empty);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email) {
            toast.error('Please fill in name and email.');
            return;
        }
        setSubmitting(true);
        try {
            await publicApi.submitDonation(form);
            setDone(true);
            setForm(empty);
            toast.success('Thank you! Our donation team will reach out shortly.');
        } catch {
            toast.error('Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div className="text-center py-8" data-testid="donation-form-success">
                <div className="h-14 w-14 rounded-full bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] mx-auto flex items-center justify-center mb-4">
                    <CheckCircle2 className="h-7 w-7" />
                </div>
                <div className="font-display font-semibold text-xl mb-1">Thank you</div>
                <p className="text-sm text-muted-foreground">
                    We&apos;ve received your donation intent. Our team will contact you with next
                    steps and receipt info.
                </p>
                <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => setDone(false)}
                    data-testid="donation-form-reset"
                >
                    Submit another inquiry
                </Button>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="space-y-4" data-testid="donation-form">
            <div className="grid sm:grid-cols-2 gap-4">
                <div>
                    <Label htmlFor="d-name">Full name *</Label>
                    <Input
                        id="d-name"
                        value={form.name}
                        onChange={(e) => update('name', e.target.value)}
                        required
                        data-testid="donation-input-name"
                    />
                </div>
                <div>
                    <Label htmlFor="d-email">Email *</Label>
                    <Input
                        id="d-email"
                        type="email"
                        value={form.email}
                        onChange={(e) => update('email', e.target.value)}
                        required
                        data-testid="donation-input-email"
                    />
                </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
                <div>
                    <Label htmlFor="d-phone">Phone</Label>
                    <Input
                        id="d-phone"
                        value={form.phone}
                        onChange={(e) => update('phone', e.target.value)}
                        data-testid="donation-input-phone"
                    />
                </div>
                <div>
                    <Label>Donation type</Label>
                    <Select
                        value={form.donation_type}
                        onValueChange={(v) => update('donation_type', v)}
                    >
                        <SelectTrigger data-testid="donation-select-type">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {TYPES.map((t) => (
                                <SelectItem key={t.value} value={t.value}>
                                    {t.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div>
                <Label htmlFor="d-amount">Approx. amount (optional)</Label>
                <Input
                    id="d-amount"
                    value={form.amount}
                    onChange={(e) => update('amount', e.target.value)}
                    placeholder="e.g. ₹5,000 or 'Not sure yet'"
                    data-testid="donation-input-amount"
                />
            </div>
            <div>
                <Label htmlFor="d-message">Message / earmark cause</Label>
                <Textarea
                    id="d-message"
                    rows={3}
                    value={form.message}
                    onChange={(e) => update('message', e.target.value)}
                    placeholder="e.g. Please use for education scholarships."
                    data-testid="donation-input-message"
                />
            </div>
            <Button
                type="submit"
                disabled={submitting}
                className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90 font-semibold"
                data-testid="donation-submit-button"
            >
                {submitting ? (
                    <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting...
                    </>
                ) : (
                    <>
                        <Send className="h-4 w-4 mr-2" /> Submit Donation Inquiry
                    </>
                )}
            </Button>
        </form>
    );
};

export default DonationForm;
